import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS, USAGE_EVENT_TYPES } from './firestore-schema';

/**
 * Record a usage event. Kept forever as per requirements.
 * @param {{ userId: string, userEmail: string, toolId: string, eventType: string, payload?: object }} params
 */
export async function recordUsageEvent({ userId, userEmail, toolId, eventType, payload }) {
  if (!db) return;
  const col = collection(db, COLLECTIONS.TOOL_USAGE);
  await addDoc(col, {
    userId,
    userEmail: (userEmail || '').toLowerCase().trim(),
    toolId,
    eventType,
    payload: payload || null,
    createdAt: serverTimestamp(),
  });
}

/**
 * Record tool open (call when agent opens a tool page).
 */
export function recordToolOpen(userId, userEmail, toolId) {
  return recordUsageEvent({
    userId,
    userEmail,
    toolId,
    eventType: USAGE_EVENT_TYPES.TOOL_OPEN,
  });
}

/**
 * Record step completed (e.g. "report generated").
 */
export function recordStepCompleted(userId, userEmail, toolId, stepName, payload) {
  return recordUsageEvent({
    userId,
    userEmail,
    toolId,
    eventType: USAGE_EVENT_TYPES.STEP_COMPLETED,
    payload: { stepName, ...payload },
  });
}

/**
 * Record report generated.
 */
export function recordReportGenerated(userId, userEmail, toolId, payload) {
  return recordUsageEvent({
    userId,
    userEmail,
    toolId,
    eventType: USAGE_EVENT_TYPES.REPORT_GENERATED,
    payload: payload || null,
  });
}

/**
 * Record report shared (e.g. email).
 */
export function recordReportShared(userId, userEmail, toolId, payload) {
  return recordUsageEvent({
    userId,
    userEmail,
    toolId,
    eventType: USAGE_EVENT_TYPES.REPORT_SHARED,
    payload: payload || null,
  });
}

/**
 * Record PDF export.
 */
export function recordExportPdf(userId, userEmail, toolId, payload) {
  return recordUsageEvent({
    userId,
    userEmail,
    toolId,
    eventType: USAGE_EVENT_TYPES.EXPORT_PDF,
    payload: payload || null,
  });
}

/**
 * Record time spent (optional; call periodically or on leave).
 */
export function recordTimeSpent(userId, userEmail, toolId, seconds, payload) {
  return recordUsageEvent({
    userId,
    userEmail,
    toolId,
    eventType: USAGE_EVENT_TYPES.TIME_SPENT,
    payload: { seconds, ...payload },
  });
}

/**
 * Fetch usage events for admin dashboard.
 * Uses orderBy + limit only (no composite index required); filters applied in memory.
 * @param {{ toolId?: string, userId?: string, startDate?: Date, endDate?: Date, maxResults?: number }} filters
 */
export async function getUsageEvents(filters = {}) {
  if (!db) return [];
  const col = collection(db, COLLECTIONS.TOOL_USAGE);
  const q = query(
    col,
    orderBy('createdAt', 'desc'),
    limit(filters.maxResults || 500)
  );
  const snap = await getDocs(q);
  let events = snap.docs.map((d) => {
    const data = d.data();
    return { id: d.id, ...data, createdAt: data.createdAt };
  });
  if (filters.toolId) events = events.filter((e) => e.toolId === filters.toolId);
  if (filters.userId) events = events.filter((e) => e.userId === filters.userId);
  if (filters.userEmail) events = events.filter((e) => (e.userEmail || '').toLowerCase() === (filters.userEmail || '').toLowerCase());
  if (filters.startDate) {
    const t = filters.startDate.getTime ? filters.startDate.getTime() : filters.startDate;
    events = events.filter((e) => e.createdAt && (e.createdAt.toMillis ? e.createdAt.toMillis() >= t : new Date(e.createdAt).getTime() >= t));
  }
  if (filters.endDate) {
    const t = filters.endDate.getTime ? filters.endDate.getTime() : filters.endDate;
    events = events.filter((e) => e.createdAt && (e.createdAt.toMillis ? e.createdAt.toMillis() <= t : new Date(e.createdAt).getTime() <= t));
  }
  return events;
}

export { USAGE_EVENT_TYPES };
